import { useTranslation } from '@pancakeswap/localization'
import { Box, Button, Card, ErrorState, Heading, NextLinkFromReactRouter, Skeleton, Text } from '@pancakeswap/uikit'
import { fromUnixTime } from 'date-fns'
import { useMemo, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import type { DexId } from 'multiswap/types'
import type { SmartSwapRange, SmartSwapReport, SmartSwapTrade } from 'server/multiswapRecord/smartSwap'
import { useSmartSwap } from 'state/infoHub/hooks'
import { getBlockExploreLink } from 'utils'
import BarChart from 'views/Info/components/InfoCharts/BarChart'
import { DEX_LOGOS } from 'views/Swap/V3Swap/utils/priceHunt'

/**
 * Markets → Smart Swap (Bez 2026-10-06; replaces the PancakeSwap tab). The proof that CADINU sends every swap to the best of five DEXes
 * for free, from the MultiSwap trade records only (server/multiswapRecord/smartSwap.ts):
 *   1. headline numbers that do not depend on volume: better than PancakeSwap on N % of trades, the average lead, trades per DEX;
 *   2. every verified trade (time, pair, DEX, USD, lead vs the next best and vs PancakeSwap, the tx on BscScan; wallets never in full);
 *   3. the extra users received, an ESTIMATE at quote time (lead × output USD), with a daily chart.
 * Small numbers are shown as they are, with plain empty states; nothing is inferred for trades that did not record a comparison.
 */

const DEXES: DexId[] = ['cadinu', 'pancakeswap', 'uniswap', 'biswap', 'bakeryswap']
const DEX_NAME: Record<DexId, string> = { cadinu: 'CADINUdex', pancakeswap: 'PancakeSwap', uniswap: 'Uniswap', biswap: 'Biswap', bakeryswap: 'BakerySwap' }
const RANGES: Array<[SmartSwapRange, string]> = [
  ['24h', '24h'],
  ['7d', '7d'],
  ['all', 'All'],
]
/** Below this many measured trades the tiles say the numbers are early. */
const EARLY = 10
const PAGE = 10

// ---------- formatting ----------
/** $ with two decimals; under $1 two significant digits ($0.0042); compact from $10K. */
export const money = (v: number): string => {
  if (v === 0) return '$0.00'
  if (v < 1) return `$${Number(v.toPrecision(2))}`
  if (v < 10_000) return `$${v.toFixed(2)}`
  return `$${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(v)}`
}
/** basis points → "0.42" (two decimals). */
const pct = (bps: number) => (bps / 100).toFixed(2)
const when = (t: number, nowS: number): string => {
  const s = Math.max(0, nowS - t)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.round(s / 60)} min ago`
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`
  return new Date(t * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
}
const rangeText = (r: SmartSwapRange) => (r === '24h' ? 'in the last 24 hours' : r === '7d' ? 'in the last 7 days' : 'yet')

// ---------- styles ----------
const sweep = keyframes`
  from { transform: translateX(-60%); }
  to { transform: translateX(160%); }
`
const reduced = '@media (prefers-reduced-motion: reduce)'

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  border-radius: 24px;
  padding: 20px 16px;
  border: 1px solid ${({ theme }) => (theme.isDark ? 'rgba(255, 194, 74, 0.35)' : 'rgba(138, 90, 0, 0.25)')};
  background: ${({ theme }) =>
    theme.isDark
      ? 'radial-gradient(120% 140% at 0% 0%, rgba(140, 124, 255, 0.38) 0%, rgba(140, 124, 255, 0) 55%), radial-gradient(90% 120% at 100% 100%, rgba(127, 212, 232, 0.28) 0%, rgba(127, 212, 232, 0) 60%), #17142B'
      : 'radial-gradient(120% 140% at 0% 0%, rgba(91, 18, 201, 0.14) 0%, rgba(91, 18, 201, 0) 55%), radial-gradient(90% 120% at 100% 100%, rgba(10, 111, 136, 0.14) 0%, rgba(10, 111, 136, 0) 60%), #FFFFFF'};
  box-shadow: 0 18px 48px -24px ${({ theme }) => (theme.isDark ? 'rgba(140, 124, 255, 0.65)' : 'rgba(91, 18, 201, 0.25)')};
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 28px 32px;
  }
`
const HeroGrid = styled.div`
  position: relative;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr);
  align-items: center;
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
    gap: 32px;
  }
`
const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => (theme.isDark ? '#1A1300' : '#FFFFFF')};
  background: ${({ theme }) => theme.colors.gold};
  box-shadow: 0 0 16px -2px ${({ theme }) => (theme.isDark ? 'rgba(255, 194, 74, 0.7)' : 'rgba(138, 90, 0, 0.4)')};
`
const HeroTitle = styled.h2`
  margin: 12px 0 8px;
  font-size: 26px;
  line-height: 1.15;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 34px;
  }
  & > em {
    font-style: normal;
    color: ${({ theme }) => theme.colors.gold};
  }
`
const HeroText = styled.p`
  margin: 0;
  max-width: 56ch;
  font-size: 15px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSubtle};
`
const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`
/** The five DEX logos of the price-hunt strip, bigger: the race CADINU runs on every swap. */
const Race = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  padding: 14px 12px;
  border-radius: 20px;
  overflow: hidden;
  background: ${({ theme }) => (theme.isDark ? 'rgba(10, 8, 24, 0.55)' : 'rgba(91, 18, 201, 0.05)')};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  &::after {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 40%;
    pointer-events: none;
    background: linear-gradient(90deg, transparent, rgba(127, 212, 232, 0.18), rgba(140, 124, 255, 0.22), transparent);
    animation: ${sweep} 3.2s ease-in-out infinite;
    ${reduced} {
      animation: none;
      opacity: 0;
    }
  }
`
const Racer = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
  figcaption {
    font-size: 12px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text};
    white-space: nowrap;
    /* five names do not fit a phone row: the logos speak there, the names stay for screen readers */
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }
  ${({ theme }) => theme.mediaQueries.md} {
    figcaption {
      position: static;
      width: auto;
      height: auto;
      clip: auto;
      max-width: 100%;
      text-overflow: ellipsis;
    }
  }
`
const Ring = styled.span<{ $lead?: boolean }>`
  display: inline-flex;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  padding: 3px;
  background: ${({ theme, $lead }) => ($lead ? `linear-gradient(135deg, ${theme.colors.gold}, #FF8A3D)` : 'rgba(255, 255, 255, 0.12)')};
  box-shadow: ${({ $lead }) => ($lead ? '0 0 18px -2px rgba(255, 194, 74, 0.75)' : '0 4px 14px -6px rgba(0, 0, 0, 0.6)')};
  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #fff;
    object-fit: cover;
  }
  ${({ theme }) => theme.mediaQueries.sm} {
    width: 56px;
    height: 56px;
  }
`
const RangeGroup = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.input};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
`
const RangeBtn = styled.button<{ $on: boolean }>`
  all: unset;
  box-sizing: border-box;
  min-width: 52px;
  min-height: 40px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme, $on }) => ($on ? theme.colors.invertedContrast : theme.colors.textSubtle)};
  background: ${({ theme, $on }) => ($on ? theme.colors.primary : 'transparent')};
  &:hover {
    color: ${({ theme, $on }) => ($on ? theme.colors.invertedContrast : theme.colors.text)};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`
const Tiles = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 16px;
  grid-template-columns: minmax(0, 1fr);
  ${({ theme }) => theme.mediaQueries.md} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }
`
const Tile = styled.div<{ $accent: string }>`
  position: relative;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 18px;
  border-radius: 20px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  box-shadow: 0 10px 28px -18px ${({ $accent }) => $accent};
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 4px;
    background: linear-gradient(90deg, ${({ $accent }) => $accent}, transparent 90%);
  }
  &::after {
    content: '';
    position: absolute;
    right: -40px;
    top: -40px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    pointer-events: none;
    background: radial-gradient(circle, ${({ $accent }) => $accent} 0%, transparent 70%);
    opacity: 0.18;
  }
`
const TileLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textSubtle};
`
const Big = styled.span<{ $tone?: 'gold' | 'success' | 'text' }>`
  font-size: 36px;
  line-height: 1.1;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
  color: ${({ theme, $tone }) => ($tone === 'gold' ? theme.colors.gold : $tone === 'success' ? theme.colors.success : theme.colors.text)};
  ${({ theme }) => theme.mediaQueries.md} {
    font-size: 40px;
  }
  small {
    margin-left: 6px;
    font-size: 15px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text};
  }
`
const Pending = styled.span`
  font-size: 18px;
  line-height: 1.3;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
`
const Hint = styled.span`
  font-size: 13px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textSubtle};
`
const Meter = styled.div`
  height: 8px;
  margin: 6px 0 2px;
  border-radius: 999px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.input};
  & > span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.success}, ${({ theme }) => theme.colors.gold});
    box-shadow: 0 0 12px -2px ${({ theme }) => theme.colors.success};
  }
`
const Chips = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 4px 0 0;
  padding: 0;
  list-style: none;
`
const Chip = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 12px 0 4px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => (theme.isDark ? 'rgba(127, 212, 232, 0.12)' : 'rgba(10, 111, 136, 0.08)')};
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  img {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #fff;
  }
`
const SectionHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 16px;
  margin: 32px 0 12px;
`
const List = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
`
const cols = css`
  grid-template-columns: 112px minmax(0, 1.1fr) minmax(0, 1.2fr) 100px minmax(0, 1.6fr) 44px;
`
const HeadRow = styled.div`
  display: none;
  ${({ theme }) => theme.mediaQueries.lg} {
    display: grid;
    ${cols}
    gap: 12px;
    padding: 12px 16px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.textSubtle};
    border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
    & > span:nth-child(4) {
      text-align: right;
    }
  }
`
/** One trade: a compact two-line card on a phone, a table row from lg. */
const TradeRow = styled.li`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto 44px;
  grid-template-areas:
    'logo pair usd tx'
    'logo meta meta tx'
    'logo gain gain tx';
  gap: 4px 12px;
  align-items: center;
  padding: 12px 8px 12px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
  &:first-child {
    border-top: 0;
  }
  .time {
    grid-area: meta;
  }
  .pair {
    grid-area: pair;
  }
  .dex {
    grid-area: logo;
  }
  .dex > span {
    display: none;
  }
  .usd {
    grid-area: usd;
    text-align: right;
  }
  .gain {
    grid-area: gain;
  }
  /* a phone line has room for the DEX and the time; the shortened wallet shows from lg */
  .time .who {
    display: none;
  }
  .tx {
    grid-area: tx;
  }
  ${({ theme }) => theme.mediaQueries.lg} {
    ${cols}
    grid-template-areas: none;
    gap: 12px;
    padding: 12px 16px;
    .time,
    .pair,
    .dex,
    .usd,
    .gain,
    .tx {
      grid-area: auto;
    }
    .dex > span {
      display: inline;
    }
    .time {
      white-space: normal;
    }
    /* the DEX has its own column from lg: the time cell is the time over the shortened wallet */
    .time > .mvenue {
      display: none;
    }
    .time > .when,
    .time > .who {
      display: block;
    }
  }
`
const Cell = styled.span`
  min-width: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const Muted = styled(Cell)`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSubtle};
  & > span {
    display: inline;
  }
`
const DexCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  img {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.cardBorder};
  }
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`
const Gains = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
`
const Gain = styled.span<{ $tone: 'gain' | 'flat' }>`
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  ${({ theme, $tone }) =>
    $tone === 'gain'
      ? css`
          color: ${theme.isDark ? '#062014' : '#FFFFFF'};
          background: ${theme.colors.success};
        `
      : css`
          color: ${theme.colors.text};
          background: ${theme.isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};
        `}
`
const TxLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  &:hover {
    background: ${({ theme }) => theme.colors.input};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`
const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 24px 16px;
`
const More = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
`
const ChartWait = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  text-align: center;
  border-radius: 16px;
  border: 1px dashed ${({ theme }) => theme.colors.cardBorder};
`
const ExtraGrid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr);
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: minmax(0, 1fr) minmax(0, 2fr);
    align-items: stretch;
  }
`
const How = styled.details`
  margin-top: 16px;
  font-size: 14px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSubtle};
  max-width: 80ch;
  summary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    cursor: pointer;
    list-style: none;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text};
    &::-webkit-details-marker {
      display: none;
    }
    &::before {
      content: '';
      border-left: 6px solid currentColor;
      border-top: 5px solid transparent;
      border-bottom: 5px solid transparent;
      transition: transform 120ms ease-out;
    }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.colors.primary};
      outline-offset: 2px;
      border-radius: 4px;
    }
  }
  &[open] summary::before {
    transform: rotate(90deg);
  }
  p {
    margin: 6px 0 0;
  }
`

// ---------- parts ----------
function GainChips({ row }: { row: SmartSwapTrade }) {
  const items: React.ReactNode[] = []
  if (row.gainBps !== null)
    items.push(
      <Gain key="next" $tone={row.gainBps > 0 ? 'gain' : 'flat'}>
        {row.gainBps > 0 ? `+${pct(row.gainBps)}% vs next best` : 'Lead <0.01% vs next best'}
      </Gain>,
    )
  if (row.vsPancakeBps !== null && row.dexId !== 'pancakeswap')
    items.push(
      <Gain key="pcs" $tone={row.vsPancakeBps > 0 ? 'gain' : 'flat'}>
        {row.vsPancakeBps > 0 ? `+${pct(row.vsPancakeBps)}% vs PancakeSwap` : 'Lead <0.01% vs PancakeSwap'}
      </Gain>,
    )
  if (row.vsPancakeBps !== null && row.dexId === 'pancakeswap') items.push(<Gain key="pcs" $tone="flat">PancakeSwap was best</Gain>)
  return items.length ? <Gains>{items}</Gains> : <Muted as="span">No quote comparison recorded</Muted>
}

function Trades({ report }: { report: SmartSwapReport }) {
  const [shown, setShown] = useState(PAGE)
  const nowS = Math.floor(Date.now() / 1000)
  const rows = report.rows.slice(0, shown)
  return (
    <Card>
      <HeadRow aria-hidden="true">
        <span>Time</span>
        <span>Pair</span>
        <span>DEX</span>
        <span>Amount</span>
        <span>Price lead at quote time</span>
        <span />
      </HeadRow>
      <List data-testid="ss-trades">
        {rows.map((r) => (
          <TradeRow key={r.tx} data-testid="ss-trade" data-dex={r.dexId}>
            <Muted className="time" title={new Date(r.t * 1000).toUTCString()}>
              <span className="mvenue">{r.venue} · </span>
              <span className="when">{when(r.t, nowS)}</span>
              <span className="who">{r.wallet}</span>
            </Muted>
            <Cell className="pair" style={{ fontWeight: 800 }}>
              {r.pair}
            </Cell>
            <DexCell className="dex">
              <img src={DEX_LOGOS[r.dexId]} alt={DEX_NAME[r.dexId]} width={32} height={32} loading="lazy" />
              <span>{r.venue}</span>
            </DexCell>
            <Cell className="usd" style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
              {r.usd === null ? <Muted as="span">no USD price</Muted> : money(r.usd)}
            </Cell>
            <span className="gain" style={{ display: 'flex', minWidth: 0 }}>
              <GainChips row={r} />
            </span>
            <TxLink className="tx" href={getBlockExploreLink(r.tx, 'transaction', 56)} target="_blank" rel="noopener noreferrer" aria-label={`Transaction ${r.tx.slice(0, 10)}… on BscScan`}>
              ↗
            </TxLink>
          </TradeRow>
        ))}
      </List>
      {report.rows.length > shown && (
        <More>
          <Button scale="sm" variant="secondary" onClick={() => setShown((n) => n + PAGE * 2)} style={{ minHeight: 44 }}>
            Show more ({report.rows.length - shown} more)
          </Button>
        </More>
      )}
      {report.trades > report.rows.length && (
        <Text fontSize="13px" color="textSubtle" p="0 16px 12px">
          The newest {report.rows.length} of {report.trades} trades are listed.
        </Text>
      )}
    </Card>
  )
}

function ExtraCard({ report }: { report: SmartSwapReport }) {
  const [hover, setHover] = useState<number | undefined>()
  const [hoverDate, setHoverDate] = useState<string | undefined>()
  const data = useMemo(() => report.extra.daily.map((d) => ({ time: fromUnixTime(d.date), value: d.usd })), [report.extra.daily])
  const { usd, counted, unpriced } = report.extra
  return (
    <Card data-testid="ss-extra">
      <Box p={['16px', null, '24px']}>
        <ExtraGrid>
          <Box>
            <TileLabel>Extra received by Smart Swap users</TileLabel>
            <Box mt="4px">
              {usd === null ? (
                <Pending>No estimate yet</Pending>
              ) : (
                <Big $tone="success">{money(hover ?? usd)}</Big>
              )}
            </Box>
            <Hint as="p" style={{ margin: '6px 0 0' }}>
              {hoverDate ??
                (usd === null
                  ? 'It starts with the first trade that records its price lead and a USD price for what it received.'
                  : `Over ${counted} trade${counted === 1 ? '' : 's'} ${report.range === 'all' ? 'so far' : rangeText(report.range)}.`)}
            </Hint>
            <Hint as="p" style={{ margin: '10px 0 0', color: 'inherit' }}>
              <b>Estimate at quote time:</b> each trade&apos;s lead over the next best DEX, as the price-hunt strip showed it when the user signed,
              × the USD value of what the trade received. Not a measured on-chain amount.
            </Hint>
            {unpriced > 0 && (
              <Hint as="p" style={{ margin: '6px 0 0' }}>
                {unpriced === 1 ? '1 trade without a USD price is not counted.' : `${unpriced} trades without a USD price are not counted.`}
              </Hint>
            )}
          </Box>
          <Box height="220px" data-testid="ss-extra-chart" data-points={data.length}>
            {data.length >= 2 ? (
              <BarChart data={data} setHoverValue={setHover} setHoverDate={setHoverDate} />
            ) : (
              <ChartWait>
                <Hint>{data.length === 1 ? 'The daily chart starts with a second day of trades.' : 'The daily chart appears with the first estimate.'}</Hint>
              </ChartWait>
            )}
          </Box>
        </ExtraGrid>
      </Box>
    </Card>
  )
}

/** Everything under the hero, from one report. Pure props (tested without network). */
export const SmartSwapBody: React.FC<{ report?: SmartSwapReport; range: SmartSwapRange; onRange: (r: SmartSwapRange) => void; loading?: boolean; error?: Error; onRetry?: () => void }> = ({
  report,
  range,
  onRange,
  loading,
  error,
  onRetry,
}) => {
  const { t } = useTranslation()
  const leader = report?.byDex[0]?.dexId
  const vs = report?.vsPancake
  const early = (n: number) => (n > 0 && n < EARLY ? ` Early numbers: ${n} trade${n === 1 ? '' : 's'} so far.` : '')
  return (
    <Box>
      <Hero aria-labelledby="ss-title">
        <HeroGrid>
          <Box>
            <Eyebrow>{t('Smart Swap')}</Eyebrow>
            <HeroTitle id="ss-title">
              {t('The best price across 5 DEXes.')} <em>{t('Free.')}</em>
            </HeroTitle>
            <HeroText>
              {t('Every swap on CADINU asks CADINUdex, PancakeSwap, Uniswap, Biswap and BakerySwap (8 routes) for a live quote and sends yours to the best one. No extra fee for the search.')}
            </HeroText>
            <HeroActions>
              <Button as={NextLinkFromReactRouter} to="/swap" scale="md" style={{ minHeight: 44 }}>
                {t('Try Smart Swap')}
              </Button>
              <RangeGroup role="group" aria-label={t('Time range')} data-testid="ss-ranges">
                {RANGES.map(([k, label]) => (
                  <RangeBtn key={k} type="button" $on={range === k} aria-pressed={range === k} onClick={() => onRange(k)} data-testid={`ss-range-${k}`}>
                    {label}
                  </RangeBtn>
                ))}
              </RangeGroup>
            </HeroActions>
          </Box>
          <Race aria-label={t('The five DEXes compared on every swap')}>
            {DEXES.map((d) => (
              <Racer key={d}>
                <Ring $lead={d === leader}>
                  <img src={DEX_LOGOS[d]} alt="" width={56} height={56} />
                </Ring>
                <figcaption>{DEX_NAME[d]}</figcaption>
              </Racer>
            ))}
          </Race>
        </HeroGrid>
      </Hero>

      {loading && !report ? (
        <Tiles>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height="132px" />
          ))}
        </Tiles>
      ) : error && !report ? (
        <Box mt="16px">
          <ErrorState title={t('Smart Swap stats did not load')} description={t('Check your connection and try again.')} onRetry={onRetry} retryLabel={t('Retry')} />
        </Box>
      ) : report ? (
        <>
          <Tiles>
            <Tile $accent="#39D98A" data-testid="ss-better">
              <TileLabel>{t('Better than PancakeSwap')}</TileLabel>
              {vs && vs.sharePct !== null ? (
                <>
                  <Big $tone="success">
                    {Math.round(vs.sharePct)}%<small>{t('of trades')}</small>
                  </Big>
                  <Meter aria-hidden="true">
                    <span style={{ width: `${Math.max(2, Math.round(vs.sharePct))}%` }} />
                  </Meter>
                  <Hint>
                    {vs.better} of {vs.measured} trades with a PancakeSwap quote got more than PancakeSwap offered.{early(vs.measured)}
                  </Hint>
                </>
              ) : (
                <>
                  <Pending>{t('Measured from the next trade')}</Pending>
                  <Hint>{t('Each new swap records PancakeSwap’s best quote next to the route it took. Older trades did not, so they are not counted.')}</Hint>
                </>
              )}
            </Tile>
            <Tile $accent="#FFC24A" data-testid="ss-avg">
              <TileLabel>{t('Average gain per trade')}</TileLabel>
              {vs && vs.avgBps !== null ? (
                <>
                  <Big $tone="gold">
                    +{pct(vs.avgBps)}%<small>{t('vs PancakeSwap')}</small>
                  </Big>
                  <Hint>
                    {report.nextBest.avgBps !== null ? `+${pct(report.nextBest.avgBps)}% vs the next best DEX, over ${report.nextBest.measured} trades.` : ''}
                    {' '}
                    {t('At quote time.')}
                  </Hint>
                </>
              ) : report.nextBest.avgBps !== null ? (
                <>
                  <Big $tone="gold">
                    +{pct(report.nextBest.avgBps)}%<small>{t('vs next best')}</small>
                  </Big>
                  <Hint>
                    Over {report.nextBest.measured} trades, at quote time. {t('Measured from the next trade')} vs PancakeSwap.
                  </Hint>
                </>
              ) : (
                <>
                  <Pending>{t('Measured from the next trade')}</Pending>
                  <Hint>{t('The lead each swap had over the other DEXes when the user signed.')}</Hint>
                </>
              )}
            </Tile>
            <Tile $accent="#7FD4E8" data-testid="ss-dex">
              <TileLabel>{t('Trades by DEX')}</TileLabel>
              {report.byDex.length ? (
                <>
                  <Chips>
                    {report.byDex.map((d) => (
                      <Chip key={d.dexId} data-testid="ss-dex-chip" data-dex={d.dexId} title={DEX_NAME[d.dexId]}>
                        <img src={DEX_LOGOS[d.dexId]} alt={DEX_NAME[d.dexId]} width={28} height={28} />
                        {d.trades}
                      </Chip>
                    ))}
                  </Chips>
                  <Hint>
                    {report.trades} verified trade{report.trades === 1 ? '' : 's'} {report.range === 'all' ? 'so far' : rangeText(report.range)}. {t('The route goes wherever the price is best.')}
                  </Hint>
                </>
              ) : (
                <>
                  <Pending>{t('No trades in this range')}</Pending>
                  <Hint>{t('Verified on-chain trades from this site show here.')}</Hint>
                </>
              )}
            </Tile>
          </Tiles>

          <SectionHead>
            <Heading scale="md" as="h3">
              {t('Every Smart Swap trade')}
            </Heading>
            <Text fontSize="13px" color="textSubtle">
              {t('Sent from this site, verified on-chain after %n% confirmations.', { n: report.confirmationsRequired })}
              {report.pendingCount > 0 ? ` ${report.pendingCount} confirming.` : ''}
            </Text>
          </SectionHead>
          {report.trades === 0 ? (
            <Card>
              <Empty data-testid="ss-empty">
                <Text bold fontSize="18px">
                  No verified trades {rangeText(range)}.
                </Text>
                <Text color="textSubtle" fontSize="14px">
                  {range === 'all' ? t('The first Smart Swap trade from this site will show here.') : t('Pick a longer range, or make the next one.')}
                </Text>
                <Button as={NextLinkFromReactRouter} to="/swap" scale="sm" style={{ minHeight: 44 }}>
                  {t('Try Smart Swap')}
                </Button>
              </Empty>
            </Card>
          ) : (
            <Trades report={report} />
          )}

          <SectionHead>
            <Heading scale="md" as="h3">
              {t('Extra received')}
            </Heading>
            <Text fontSize="13px" color="textSubtle">
              {t('An estimate at quote time')}
            </Text>
          </SectionHead>
          <ExtraCard report={report} />

          <How>
            <summary>{t('How Smart Swap is measured')}</summary>
            <p>
              Every trade listed was sent from this site and checked on BNB Chain: the transaction, the router it went to, the tokens and the
              amount received. Amounts in USD are priced at the trade&apos;s block.
            </p>
            <p>
              The price leads are what the price-hunt strip showed when the user signed: the winning route against the next best DEX and,
              for trades since 6 Oct 2026, against PancakeSwap&apos;s best quote. They are kept only for a verified trade on the winning DEX
              and capped at 5%. A trade that did not record a comparison is left out of that number; nothing is filled in.
            </p>
          </How>
        </>
      ) : null}
    </Box>
  )
}

export const SmartSwapView: React.FC = () => {
  const [range, setRange] = useState<SmartSwapRange>('all')
  const { report, error, isLoading, retry } = useSmartSwap(range)
  return <SmartSwapBody report={report} range={range} onRange={setRange} loading={isLoading} error={error} onRetry={retry} />
}

export default SmartSwapView
